import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type Procedencia, type ProcedenciaRequest } from '../types/procedencia';
import { ProcedenciaService } from '../services/procedenciaService';

interface ProcedenciaState {
  procedencias: Procedencia[];
  loading: boolean;
  error: string | null;
  selectedProcedencia: Procedencia | null;
  dialogOpen: boolean;
  fetchProcedencias: () => Promise<void>;
  saveProcedencia: (procedencia: ProcedenciaRequest) => Promise<void>;
  deleteProcedencia: (id: number) => Promise<void>;
  setSelectedProcedencia: (procedencia: Procedencia | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useProcedenciaStore = create<ProcedenciaState>()(
  devtools(
    (set, get) => ({
      procedencias: [],
      loading: false,
      error: null,
      selectedProcedencia: null,
      dialogOpen: false,

      fetchProcedencias: async () => {
        set({ loading: true, error: null });
        try {
          const procedencias = await ProcedenciaService.getProcedencias();
          set({ procedencias, loading: false });
        } catch (error) {
          set({ error: 'Error al cargar procedencias', loading: false });
          console.error('Error fetching procedencias:', error);
        }
      },

      saveProcedencia: async (procedencia: ProcedenciaRequest) => {
        set({ loading: true, error: null });
        try {
          await ProcedenciaService.saveProcedencia(procedencia);
          await get().fetchProcedencias();
          set({ loading: false, dialogOpen: false, selectedProcedencia: null });
        } catch (error) {
          set({ error: 'Error al guardar procedencia', loading: false });
          console.error('Error saving procedencia:', error);
          throw error;
        }
      },

      deleteProcedencia: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await ProcedenciaService.deleteProcedencia(id);
          set((state) => ({
            procedencias: state.procedencias.filter((p) => p.idProcedencia !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: 'Error al eliminar procedencia', loading: false });
          console.error('Error deleting procedencia:', error);
          throw error;
        }
      },

      setSelectedProcedencia: (procedencia: Procedencia | null) => set({ selectedProcedencia: procedencia }),
      openDialog: () => set({ dialogOpen: true }),
      closeDialog: () => set({ dialogOpen: false, selectedProcedencia: null }),
      clearError: () => set({ error: null }),
    }),
    { name: 'procedencia-store' }
  )
);
